import type { LogLevel, UserIdType } from '../types.js'
import { UserIdCtor } from '../user-id.js'
import { assignCommonProperties } from '../util/data/assign-common-properties.js'

import { format } from 'date-fns'
import { jsonProperty, jsonClass } from '@sumbricht/json-class-serializer'

export const LogEntryTypeParams: Record<
	LogLevel,
	{
		typeString: string
		priority: number
		consoleFormat: string
	}
> = {
	critical: {
		typeString: 'CRITICAL',
		priority: 1,
		consoleFormat: '\x1b[37m\x1b[41m\x1b[1m%s\x1b[0m',
	}, // white on red, bold
	error: {
		typeString: 'ERROR',
		priority: 2,
		consoleFormat: '\x1b[31m\x1b[1m%s\x1b[0m',
	}, // red, bold
	warning: {
		typeString: 'warn',
		priority: 3,
		consoleFormat: '\x1b[33m%s\x1b[0m',
	}, // yellow
	info: { typeString: 'info', priority: 4, consoleFormat: '%s' }, // normal
	debug: {
		typeString: 'debug',
		priority: 5,
		consoleFormat: '\x1b[36m%s\x1b[0m',
	}, // blue
	status: {
		typeString: 'status',
		priority: 1,
		consoleFormat: '\x1b[1m%s\x1b[0m',
	}, // bold
}

const LogComponents = [
	'Initializer',
	'Bootstrapper',
	'Repository',
	'CompilationManager',
	'AngularFrontendCompiler',
	'BackendCompiler',
	'BuildManager',
	'DbManager',
	'EventManager',
	'ServiceManager',
	'ExtensionManager',
	'Api',
	'ApiExecutor',
	'InterThreadComm.',
	'TriggerManager',
	'CertificateManager',
	'Console',
	'AppCode',
	'LicenseManager',
	'AuthManager',
	'SettingsManager',
	'Script',
] as const

const consolePadLength = {
	typeStr:
		Math.max(
			...Object.entries(LogEntryTypeParams).map(
				([k, v]) => v.typeString.length,
			),
		) + 3,
	componentStr: Math.max(...LogComponents.map((c) => c.length)) + 3,
}

export type LogComponent = (typeof LogComponents)[number]

class BaseLogEntry {
	@jsonProperty(Number)
	id?: number
	@jsonProperty(Date)
	date!: Date
	@jsonProperty(String)
	type!: LogLevel
	@jsonProperty(String)
	component?: LogComponent
	@jsonProperty(String)
	appModuleId?: string
	@jsonProperty(String)
	moduleId?: string
	@jsonProperty(String)
	message?: string
}

@jsonClass('LogEntry')
export class LogEntry extends BaseLogEntry {
	@jsonProperty(String)
	stackTrace?: string
	@jsonProperty(() => UserIdCtor)
	userId?: UserIdType

	@jsonProperty(String)
	get __type(): string {
		return 'LogEntry'
	}
	set __type(_) {
		/* ignore */
	}

	constructor(init?: Partial<LogEntry>) {
		super()
		assignCommonProperties(this, init)
	}

	formatForConsole() {
		const params = LogEntryTypeParams[this.type]

		const dateStr = format(this.date, 'yyyy-MM-dd HH:mm:ss')
		const typeStr = `(${params.typeString})`.padEnd(consolePadLength.typeStr)
		const componentStr = (this.component ? `(${this.component})` : '').padEnd(
			consolePadLength.componentStr,
		)

		let message = `[${dateStr}] ${typeStr}${componentStr}${this.message}`
		if (this.stackTrace) {
			const stackIndented = this.stackTrace
				.split('\n')
				.map((l) => `\t${l}`)
				.join('\n')
			message += `\n${stackIndented}`
		}
		return [params.consoleFormat, message]
	}
}

export class LogEntryWithoutStack extends BaseLogEntry {
	@jsonProperty(Boolean)
	hasStackTrace: boolean = false
	@jsonProperty(String)
	userName?: string

	constructor(init?: LogEntryWithoutStack) {
		super()
		assignCommonProperties(this, init)
	}
}
